import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Sidebar from '../landingPage/Sidebar';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/system';
import Cookies from 'js-cookie';

const SearchField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    height: 46,
    borderRadius: 8,
    alignItems: 'center',
    background: '#F8FAFC',
    transition: 'all 0.2s ease-in-out',

    '& fieldset': { border: '0.5px solid #e5e7eb' },
    '&:hover fieldset': { borderColor: '#d1d5db' },
    '&.Mui-focused fieldset': { borderColor: '#5965A6', borderWidth: 0.6 },
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 14px 12px 0',
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#374151',
  },

  '& .MuiInputAdornment-root': {
    marginRight: 8,
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    fontSize: 20,
    opacity: 0.85,
  },
  '& input::placeholder': {
    color: '#9CA3AF',
    opacity: 1,
  },
});

export default function Help() {
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  const rawRole = useMemo(
    () => Cookies.get('Role_id') ?? Cookies.get('role_id'),
    [],
  );

  const isSuperAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 1;
  }, [rawRole]);

  const sections = useMemo(
    () => [
      {
        id: 'getting-started',
        title: 'Getting started',
        items: [
          {
            q: 'What is this app?',
            a: '\tA no-code ETL builder to create connections, map fields, and run workflows between systems.',
          },
          {
            q: 'How do I create my first project?',
            a: '\tGo to Projects → New Project, then add workflows and connections under it.',
          },
        ],
      },

      // Login
      {
        id: 'login',
        title: 'Login',
        items: [
          {
            q: 'How do I log in?',
            a:
              '\tStepwise:\n' +
              '\t• Visit https://etl-frontend-zeta.vercel.app\n' +
              '\t• Click “Login with Username/Password” or “Continue with Google”.\n' +
              '\t• For Google, choose your account and grant access.\n' +
              '\t• For Username/Password, use your credentials or click “Sign up” to create one.\n' +
              '\t• Forgot Password is available from the login screen.',
          },
        ],
      },

      // Dashboard
      {
        id: 'dashboard',
        title: 'Dashboard',
        items: [
          {
            q: 'What does the dashboard show?',
            a:
              '\tYou’ll see: active projects, number of workflows, supported connectors, saved connections, running workflows, and flow execution history with error logs.\n' +
              '\tTables provide quick actions (Execute, Edit) per workflow.',
          },
        ],
      },

      // Projects
      {
        id: 'projects',
        title: 'Projects',
        items: [
          {
            q: 'Create, edit, delete projects ',
            a:
              '\t• Navigate to Projects → Create Project.\n' +
              '\t• Enter project name description, and click Save.\n' +
              '\t• Save to start building workflows.\n' +
              '\t• Inside the project, create a new workflow (name + description) to open the workflow canvas.\n' +
              '\t• Edit/Delete options are available per project; deletion requires reauthentication and checks for running workflows.',
          },
          {
            q: 'Star projects and workflows',
            a: '\tMark important projector workflows with Star to pin them for quick access.',
          },
        ],
      },

      // Connections
      {
        id: 'connections',
        title: 'Connections',
        items: [
          {
            q: 'Create a connection',
            a:
              '\t• Go to Connections → Create Connection.\n' +
              '\t• Select connector type for source/destination.\n' +
              '\t• Provide API details (Base URL, headers) and authentication method.\n' +
              '\t \t - OAuth2 (e.g., Salesforce, Jira Atlassian)\n' +
              ' \t\t - Basic Auth (e.g., SQL Server, Oracle)\n' +
              '\t• Click Test Connection to validate.\n' +
              '\t• Saved connection becomes reusable across workflows.',
          },
          {
            q: 'Edit or delete connections',
            a: '\tEditing and deleting require reauthentication to protect credentials.',
          },
        ],
      },

      // Workflows
      {
        id: 'workflows',
        title: 'Workflows',
        items: [
          {
            q: 'Create or run a workflow',
            a:
              '\t• From a project, click New Workflow (or open an existing file).\n' +
              '\t• On the canvas, drag ‘Field Mapping’ or ‘Decision Mapping’ from the Logic panel.\n' +
              '\t• Connect required Source and Destination nodes to the mapping node.\n' +
              '\t• Double-click each Source/Destination node to configure connection details.\n' +
              '\t• When all connections are valid, double-click the Field Mapping node to select entities. Saving entities navigates you to the Field Mapping screen.\n' +
              '\t• Optionally define execution time to schedule (UTC) or click Execute to run immediately.\n' +
              '\t• Save and enable the workflow.',
          },
          {
            q: 'Schedule workflows',
            a: '\tSet a start date/time and frequency (hourly, daily, weekly, monthly), optionally select specific days (Mon–Sun). The workflow runs automatically on schedule.',
          },
          {
            q: 'Delete workflows',
            a: '\tDeleting is a sensitive action and requires reauthentication.',
          },
          {
            q: 'Execution history',
            a: '\tUse Flow Execution to see run status (ACTIVE, SUCCESS, FAILED) and drill into run details.',
          },
        ],
      },

      // Field Mapping & transformations
      {
        id: 'mapping',
        title: 'Mapping & transformations',
        items: [
          {
            q: 'Field mapping',
            a:
              '\t• Open the workflow → double-click the Mapping node → select entities.\n' +
              '\t• On save, you’re taken to the Field Mapping screen. Fuzzy Logic is offered on first use (default 80% accuracy) and can be applied on demand.\n' +
              '\t• Drag/drop or manually connect source fields to destination fields.\n' +
              '\t• Edge colors:\n' +
              '\t  - Green : valid mapping\n' +
              '\t   - Red : data type mismatch\n' +
              '\t   - Blue : valid mapping via a manual function\n' +
              '\t• Apply transformation nodes (e.g., trim, concat, substring, date format). Functions are grouped by String/Number/Date/Utilities.',
          },
          {
            q: 'Validation',
            a: '\tPreview transformed data before running the workflow to catch schema or type issues early.',
          },
        ],
      },

      // Console
      {
        id: 'console',
        title: 'Console',
        items: [
          {
            q: 'What can I do in Console?',
            a: '\tView real-time logs for Extract, Transform, Load per mapped entity, including detailed error logs. You can refresh status or cancel an on-demand flow.',
          },
        ],
      },

      // Notifications
      {
        id: 'notifications',
        title: 'Notifications',
        items: [
          {
            q: 'What alerts are available?',
            a: '\tJob success/failure, API errors, field mismatches, and other system events are surfaced in Notifications.',
          },
        ],
      },

      // Reports
      {
        id: 'reports',
        title: 'Reports',
        items: [
          {
            q: 'How do I analyze runs?',
            a: '\tUse prebuilt and custom reports with filters and export. Dashboard → Reports & Insights shows weekly/monthly status-wise counts.',
          },
        ],
      },

      // Jira Integration
      {
        id: 'jira-integration',
        title: 'Jira Integration',
        items: [
          {
            q: 'How do failures get tracked?',
            a: '\tOn failures, the system can auto-create Jira tickets (if Jira is configured in the flow). Tickets are visible from Console. If Jira isn’t configured, tickets are not raised.',
          },
        ],
      },

      // User Roles
      // {
      //   id: 'user-roles',
      //   title: 'User Roles',
      //   items: [
      //     {
      //       q: 'What roles are supported?',
      //       a:
      //         'Admin: Full access to all modules, including user management.\n' +
      //         'User: Limited to project creation and workflow execution.',
      //     },
      //   ],
      // },

      // Security (unchanged, kept for completeness)
      {
        id: 'security',
        title: 'Security & authentication',
        items: [
          {
            q: 'Login methods',
            a: '\tAuth0 supports username/password and Google OAuth.',
          },
          {
            q: 'Reauthentication',
            a: '\tSensitive actions (delete workflow/connection/project, edit connection) require reauth.',
          },
        ],
      },

      // Trial & Subscription
      {
        id: 'trial-subscription',
        title: 'Trial & Subscription',
        items: [
          {
            q: 'What is the free trial?',
            a:
              '\tNew users and organizations receive a 14-day free trial period to explore the platform.\n' +
              '\tDuring the trial, you have access to all features with some limits:\n' +
              '\t• Maximum 5 active connectors (configurable by admin)\n' +
              '\t• Full access to create projects, workflows, and connections\n' +
              '\t• All transformation and mapping features available',
          },
          {
            q: 'How long is my trial period?',
            a:
              '\tThe default trial period is 14 days from when your account or organization is created.\n' +
              '\tYou can check your remaining trial days:\n' +
              '\t• On the dashboard (trial warning banner if 1-4 days remaining)\n' +
              '\t• Via email notifications when trial is ending',
          },
          {
            q: 'What happens when my trial expires?',
            a:
              '\tWhen your trial period ends:\n' +
              '\t• Your account becomes inactive\n' +
              '\t• You will not be able to log in\n' +
              '\t• Contact support to upgrade to a paid subscription\n' +
              '\t• Custom Support Team can reactivate your trial if needed',
          },
          {
            q: 'How do I upgrade to a paid subscription?',
            a:
              '\tTo upgrade to a paid subscription:\n' +
              '\t• Contact your Support Team administrator\n' +
              '\t• Or contact support for individual accounts\n' +
              '\t• Subscription periods available: 1, 3, 6, 12 months, or custom\n' +
              '\t• Once upgraded, all users in your organization are automatically activated',
          },
          {
            q: 'What are subscription periods?',
            a:
              '\tPaid subscriptions are available for:\n' +
              '\t• 1 month\n' +
              '\t• 3 months\n' +
              '\t• 6 months\n' +
              '\t• 12 months\n' +
              '\t• Custom period (1-365 days)\n' +
              '\tYour subscription end date is calculated from the start date plus the selected period.',
          },
          {
            q: 'What happens when my subscription expires?',
            a:
              '\tWhen your subscription period ends:\n' +
              '\t• Your account automatically becomes inactive\n' +
              '\t• You will not be able to log in\n' +
              '\t• All your data (projects, workflows, connections) is preserved\n' +
              '\t• Contact support to renew your subscription\n' +
              '\t• Customer Support Team can reactivate your account or extend your subscription',
          },
          {
            q: 'How do I check my trial or subscription status?',
            a:
              '\tYou can check your account status in several ways:\n' +
              '\t• Dashboard: Look for trial/subscription status indicators\n' +
              '\t• Trial warning banner: Appears when 1-4 days remaining\n' +
              '\t• Email notifications: Sent when trial/subscription is ending\n' +
              '\t• Login response: Shows trial status and days remaining\n' +
              '\t• Contact support: For detailed account information',
          },
          {
            q: 'What are trial warnings?',
            a:
              '\tThe system sends warnings when your trial is ending soon:\n' +
              '\t• Warning period: 1-4 days before expiration\n' +
              '\t• Warning banner: Shown on dashboard\n' +
              '\t• Email notifications: Sent to you and administrators\n' +
              '\t• Purpose: Remind you to upgrade before access is restricted',
          },
          {
            q: 'Can my trial be extended?',
            a:
              '\tYes, trial periods can be extended by Customer Support Team:\n' +
              '\t• Contact support to request an extension\n' +
              '\t• Contact support can extend by additional days\n' +
              '\t• Or set a new trial end date\n' +
              '\t• Extensions are typically granted for evaluation purposes',
          },
          {
            q: 'What if my account is inactive?',
            a:
              '\tIf your account is inactive, you cannot:\n' +
              '\t• Log in to the system\n' +
              '\t• Access your projects or workflows\n' +
              '\t• Create new connections\n' +
              '\t• Execute workflows\n\n' +
              '\tTo reactivate:\n' +
              '\t• Contact support administrator\n' +
              '\t• Upgrade to a paid subscription\n' +
              '\t• Request trial extension (if applicable)',
          },
          {
            q: 'What is the difference between trial and subscription?',
            a:
              '\tTrial:\n' +
              '\t• Free 14-day period for new users/organizations\n' +
              '\t• Limited to 5 active connectors\n' +
              '\t• Full feature access during trial period\n' +
              '\t• Automatically expires after 14 days\n\n' +
              '\tSubscription (Paid):\n' +
              '\t• Paid access with selected period (1-12 months or custom)\n' +
              '\t• No connector limits\n' +
              '\t• Full feature access\n' +
              '\t• Automatically expires after subscription period\n' +
              '\t• Can be renewed or extended',
          },
          {
            q: 'Who manages subscriptions and trials?',
            a:
              '\tSubscription and trial management:\n' +
              '\t• Support Team: Can create organizations, set subscription periods, extend trials, and manage account status, can assist with upgrades, renewals, and account reactivation',
          },
        ],
      },
      {
        id: 'contact-submissions',
        title: 'Contact Submissions Management',
        items: [
          {
            q: 'What are Contact Submissions?',
            a:
              '\tContact Submissions are inquiries submitted by users through the public contact form on the website.\n' +
              '\tThese submissions are stored in the database and can be managed by SuperAdmins in the admin panel.\n' +
              '\tEach submission includes: name, email, subject, message, status, and optional follow-up notes.',
          },
          {
            q: 'How do I access Contact Submissions?',
            a:
              '\t• Navigate to Admin Panel → Contact Submissions (SuperAdmin only)\n' +
              '\t• You will see a table listing all contact form submissions\n' +
              '\t• Submissions are sorted by creation date (newest first)\n' +
              '\t• Use pagination to navigate through multiple pages of submissions',
          },
          {
            q: 'What are the submission statuses?',
            a:
              '\t• New: Recently submitted, not yet reviewed\n' +
              '\t• In Progress: Currently being handled by a SuperAdmin\n' +
              '\t• Resolved: Issue/question has been addressed\n' +
              '\t• Archived: Submission is completed and archived',
          },
          {
            q: 'What is Contact Status and how does it work?',
            a:
              '\tContact Status tracks the communication state with the contact:\n' +
              '\t• Not Set (New): Default state when submission is created\n' +
              '\t• Contacted: Admin has reached out to the contact\n' +
              '\t• Not Interested: Contact is not interested; will auto-archive after 14 days\n' +
              '\t• Reset: Resetting from "Not Interested" changes status back to "New" and clears the not interested date\n\n' +
              '\tYou can update Contact Status via:\n' +
              '\t• Edit dialog: Select from dropdown\n' +
              '\t• Quick Actions menu: Mark as Contacted, Mark as Not Interested, or Reset',
          },
          {
            q: 'What is auto-archiving?',
            a:
              '\tSubmissions marked as "Not Interested" are automatically archived after 14 days.\n' +
              '\t• The 14-day countdown starts when Contact Status is set to "Not Interested"\n' +
              '\t• Auto-archiving runs daily at 9 AM via scheduled task\n' +
              '\t• Only submissions with Contact Status = "Not Interested" are auto-archived\n' +
              '\t• If you reset Contact Status before 14 days, the submission will not be auto-archived\n' +
              '\t• You can manually archive submissions at any time by changing status to "Archived"',
          },
          {
            q: 'How do I assign a submission to a SuperAdmin?',
            a:
              '\t• Click the Edit icon on any submission\n' +
              '\t• In the "Assigned To" dropdown, select a SuperAdmin from the list\n' +
              '\t• Click "Update" to save the assignment\n' +
              '\t• The assigned SuperAdmin will receive an email notification automatically\n' +
              '\t• You can also unassign by selecting "None (Unassign)" from the dropdown',
          },
          {
            q: 'How do I add follow-up notes?',
            a:
              '\t• Click the Edit icon on a submission\n' +
              '\t• Enter your notes in the "Follow-up Notes" text field\n' +
              '\t• Notes can include: conversation details, action items, resolution steps, etc.\n' +
              '\t• Click "Update" to save your notes\n' +
              '\t• Notes are visible to all SuperAdmins and help track the conversation history',
          },
          {
            q: 'How do I update the status of a submission?',
            a:
              '\t• Click the Edit icon on a submission\n' +
              '\t• Select a new status from the "Status" dropdown:\n' +
              '\t  - New: For newly received submissions\n' +
              '\t  - In Progress: When actively working on it\n' +
              '\t  - Resolved: When the inquiry is answered\n' +
              '\t  - Archived: When completely finished\n' +
              '\t• Click "Update" to save the status change',
          },
          {
            q: 'How do I search for specific submissions?',
            a:
              '\t• Click the search icon in the top right of the Contact Submissions page\n' +
              '\t• Type keywords to search by:\n' +
              '\t  - Name (e.g., "John")\n' +
              '\t  - Email address (e.g., "john@example.com")\n' +
              '\t  - Subject line (e.g., "pricing")\n' +
              '\t  - Phone number (e.g., "123-456-7890")\n' +
              '\t  - Follow-up notes (searches in admin notes)\n' +
              '\t• Search results update automatically as you type\n' +
              '\t• Click the X icon to clear the search\n' +
              '\t• Search is case-insensitive and matches partial text',
          },
          {
            q: 'How do I filter submissions?',
            a:
              '\tMultiple filter options are available in the filter row below the title:\n' +
              '\t• Status: Filter by New, In Progress, Resolved, or Archived\n' +
              '\t• Contact Status: Filter by Not Set, Contacted, or Not Interested\n' +
              '\t• Assigned To: Filter by specific SuperAdmin or "Unassigned"\n' +
              '\t• Date Range: Use "From" and "To" date pickers to filter by creation date\n' +
              '\t• Filters can be combined (e.g., show "New" submissions assigned to a specific admin from last week)\n' +
              '\t• All filters reset pagination to page 1',
          },
          {
            q: 'What happens when I assign a submission?',
            a:
              '\t• The submission is assigned to the selected SuperAdmin\n' +
              '\t• An email notification is automatically sent to the assigned SuperAdmin\n' +
              '\t• The email includes:\n' +
              '\t  - Submission ID and details\n' +
              '\t  - Customer name, email, and message\n' +
              '\t  - Current status\n' +
              '\t  - Link to view in admin panel\n' +
              '\t• The assignment is visible in the submission details',
          },
          {
            q: 'How do I view full submission details?',
            a:
              '\tTwo ways to view details:\n\n' +
              '\t1. Expandable Row View:\n' +
              '\t  - Click the expand/collapse icon (arrow) in the Sr No column\n' +
              '\t  - View: Phone Number, Subject, Full Message, Follow-up Notes, Last Activity, Not Interested Date\n' +
              '\t  - No need to open a dialog, details appear inline\n\n' +
              '\t2. View Dialog:\n' +
              '\t  - Click the View icon (eye icon) in Actions column\n' +
              '\t  - A dialog shows:\n' +
              '\t    - Full customer information (name, email, phone)\n' +
              '\t    - Complete message content\n' +
              '\t    - Current status and contact status with color-coded chips\n' +
              '\t    - Assigned SuperAdmin (if assigned)\n' +
              '\t    - Follow-up notes (if any)\n' +
              '\t    - Submission date and time\n' +
              '\t    - Not Interested Date (if applicable)\n' +
              '\t  - Click "Close" to return to the list',
          },
          {
            q: 'Can I delete a submission?',
            a:
              '\t• Yes, but use caution as deletion is permanent\n' +
              '\t• Click the Delete icon on a submission\n' +
              '\t• Confirm the deletion in the dialog\n' +
              '\t• Once deleted, the submission cannot be recovered\n' +
              '\t• Consider closing submissions instead of deleting to maintain records',
          },
          {
            q: 'What are Quick Actions?',
            a:
              '\tQuick Actions provide fast ways to update Contact Status:\n' +
              '\t• Click the three-dot menu (⋮) icon in the Actions column\n' +
              '\t• Available actions:\n' +
              '\t  - Mark as Contacted: Sets Contact Status to "Contacted"\n' +
              '\t  - Mark as Not Interested: Sets Contact Status to "Not Interested" (starts 14-day auto-archive timer)\n' +
              '\t  - Reset Contact Status: Resets Contact Status to "New" and clears Not Interested Date\n' +
              '\t• Quick Actions update the submission immediately without opening the edit dialog',
          },
          {
            q: 'How are email notifications sent?',
            a:
              '\t• Automatic email on contact form submission: Sent to Superadmin email\n' +
              '\t• Assignment notification: Sent to assigned SuperAdmin when submission is assigned\n' +
              '\t• Reassignment notification: Sent to previous assignee when submission is reassigned\n' +
              '\t• Email includes all submission details for quick reference\n' +
              '\t• If email sending fails, the submission is still saved in the database\n' +
              '\t• Check backend logs if emails are not being received',
          },
          {
            q: 'How do I view activity history for a submission?',
            a:
              '\t• Click the View icon (eye icon) on any submission\n' +
              '\t• In the View dialog, you can see the activity log\n' +
              '\t• Activity log shows:\n' +
              '\t  - All status changes\n' +
              '\t  - Assignment changes (who assigned to whom)\n' +
              '\t  - Contact status updates\n' +
              '\t  - Follow-up notes updates\n' +
              '\t  - Phone number updates\n' +
              '\t  - Who made each change and when\n' +
              '\t• This helps track the complete history of interactions with the submission',
          },
        ],
      },
      {
        id: 'common-faqs',
        title: 'FAQs',
        items: [
          {
            q: '• What is ETL?',
            a: "\tETL stands for Extract, Transform, Load — it's how the system moves and cleans data between ERPs.",
          },
          {
            q: '• Can I use this for any ERP?',
            a: '\tYes, as long as the ERP supports APIs (REST recommended).',
          },
          {
            q: '• What happens if an API call fails?',
            a: '\tA Jira ticket is auto-generated, and the failure is shown in the Console and Notifications.',
          },
          {
            q: '• Can I create flows with multiple sources and multiple destinations?',
            a: '\tYes, multiple sources and destinations are supported.',
          },
          {
            q: '• Where can I see failures for my flow?',
            a: '\tOn the workflow screen, the execution status shows counts and failures. Click the eye icon to view detailed logs.',
          },
          {
            q: '• Can I schedule my flows for a particular time?',
            a: '\tYes, flows can be scheduled; the schedule uses the UTC time zone.',
          },
          {
            q: '• Can I limit the data transferred from source to destination?',
            a: '\tYes, you can restrict data by applying filters on fields.',
          },
          {
            q: '• Will Jira tickets be created on failure? What if I don’t have Jira configured?',
            a: '\tIf Jira isn’t configured in the flow, no ticket will be raised.',
          },
          {
            q: '• Where can I see my flow history if it was scheduled and I log in later?',
            a: '\tOn the dashboard, open Flow Execution history. Click the workflow ID to view all execution details.',
          },
          {
            q: '• Can I save my connection to a SaaS API?',
            a: '\tYes. On the Connections screen you can save and reuse connections.',
          },
          {
            q: '• What is Fuzzy logic and how does it map fields?',
            a: '\tFuzzy logic targets ~80% accuracy. It matches entities by names/relevant meanings, then applies similar matching to fields. You can discard or adjust mappings manually.',
          },
          {
            q: '• Can I see consolidated data for a week or month?',
            a: '\tYes. On the dashboard, open Reports & Insights to view status-wise flow counts by week or month.',
          },
          {
            q: '• Can I cancel a flow execution?',
            a: '\tYes. On-demand flows can be canceled. Scheduled flows can be rescheduled or deleted.',
          },
          {
            q: '• If a flow or mapping is deleted, can I recover it?',
            a: '\tNo. Once mappings or flows are deleted, they cannot be recovered.',
          },
          {
            q: '• What transformation options are available?',
            a: '\tString, Number, and Date functions, plus utility functions for matching, lookup, and referencing external fields.',
          },
          {
            q: '• Is the app only for ERP systems?',
            a: '\tPrimarily yes, but it also integrates with tools like Jira (for ticketing/debugging) and SQL Server (on-prem) for backup/dumps without transformation.',
          },
          {
            q: '• Is there any AI assistance?',
            a: '\tYes. An AI-assisted chatbot helps you navigate and use the application.',
          },
        ],
      },
    ].filter((section) => {
      if (section.id === 'trial-subscription' || section.id === 'contact-submissions') {
        return isSuperAdmin;
      }
      return true;
    }),
    [isSuperAdmin],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections
      .map((s) => ({
        ...s,
        items: s.items.filter(
          (it) =>
            it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q),
        ),
      }))
      .filter((s) => s.items.length > 0);
  }, [query, sections]);
const HEADER_OFFSET = 60; // px — adjust to your header height

const scrollTo = (id) => {
  try {
    const container = containerRef.current;             
    const el = document.getElementById(id);
    if (!container || !el) return;

    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();

    const targetTop =
      container.scrollTop + (eRect.top - cRect.top) - HEADER_OFFSET;

    container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
  } catch (e) {
    // no-op
  }
};

  const openChat = () => {
    try {
      document.getElementById('helpdesk-floating-icon')?.click();
    } catch (e) {
      // no-op
    }
  };

  useEffect(() => {
  try {
    containerRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  } catch (err) { 
      console.debug('[Help] initial scroll skipped:', err);
  }
}, []);

  // Dynamic doc config (remote + env fallback)
  const [remoteDocUrl, setRemoteDocUrl] = useState('');
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState('');

  const envDocUrl = process.env.REACT_APP_HELP_DOC_URL || '';
  const configUrl = process.env.REACT_APP_HELP_CONFIG_URL || ''; // expected JSON: { "helpDocUrl": "https://...docx" }

  useEffect(() => {
    let cancelled = false;
    async function loadConfig() {
      if (!configUrl) {
        setRemoteDocUrl(envDocUrl || '');
        return;
      }
      setRemoteLoading(true);
      setRemoteError('');
      try {
        const resp = await fetch(configUrl, { cache: 'no-store' });
        if (!resp.ok) throw new Error(`status ${resp.status}`);
        const json = await resp.json();
        if (!cancelled) setRemoteDocUrl(json?.helpDocUrl || envDocUrl || '');
      } catch (e) {
        if (!cancelled) {
          setRemoteDocUrl(envDocUrl || '');
          setRemoteError('Could not load remote help config; using fallback.');
        }
      } finally {
        if (!cancelled) setRemoteLoading(false);
      }
    }
    loadConfig();
    return () => {
      cancelled = true;
    };
  }, [configUrl, envDocUrl]);

  const officeEmbedUrl = useMemo(() => {
    if (!remoteDocUrl) return '';
    try {
      const encoded = encodeURIComponent(remoteDocUrl);
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encoded}`;
    } catch (e) {
      return '';
    }
  }, [remoteDocUrl]);

  const [showEmbed, setShowEmbed] = useState(false);

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: '#FAFBFD',
      }}
    >
      <Sidebar />

      <Box sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            height: 'calc(100vh - 2rem)',
            bgcolor: 'transparent',
          }}
        >
          <Box
            sx={{
              width: { xs: 0, md: 280 },
              display: { xs: 'none', md: 'block' },
              bgcolor: 'background.paper',
              borderRadius: 1,
              boxShadow: '0px 4px 10px 0px #0000000D',
              px: 2,
              py: 1,
              overflowY: 'auto',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Help Center
            </Typography>
            <SearchField
              size="small"
              fullWidth
              placeholder="Search help"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.2, height: 28 }}
            />
            <Divider sx={{ mb: 1 }} />
            <List dense>
              {sections.map((s) => (
                <ListItemButton
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText
                    primary={s.title}
                    primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                  />
                </ListItemButton>
              ))}
            </List>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {remoteLoading && (
                <Typography variant="body2" color="text.secondary">
                  Loading handbook link…
                </Typography>
              )}
              {remoteError && (
                <Typography variant="caption" color="text.secondary">
                  {remoteError}
                </Typography>
              )}
              {remoteDocUrl && (
                <>
                  <Button
                    component="a"
                    href={remoteDocUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                  >
                    Open Handbook (Latest)
                  </Button>
                  {officeEmbedUrl && (
                    <Button
                      onClick={() => setShowEmbed((v) => !v)}
                      variant="outlined"
                    >
                      {showEmbed ? 'Hide Embedded Doc' : 'Show Embedded Doc'}
                    </Button>
                  )}
                </>
              )}
              {/* <Divider sx={{ my: 1 }} /> */}
              <Button
                onClick={openChat}
                variant="contained"
                sx={{ backgroundColor: '#5965A6' }}
              >
                Open AI Chat
              </Button>
              <Button
                onClick={() => navigate('/contact')}
                variant="outlined"
                sx={{ borderColor: '#5965A6', color: '#5965A6' }}
              >
                Contact Support
              </Button>
            </Box>
          </Box>

          <Box
            ref={containerRef}
            sx={{
              flex: 1,
              bgcolor: 'background.paper',
              borderRadius: 1,
              boxShadow: '0px 4px 10px 0px #0000000D',
              p: { xs: 2, sm: 3 },
              overflowY: 'auto',
              scrollBehavior: 'smooth',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                mb: 2,
              }}
            >
              <Typography
                variant={isDownMd ? 'h6' : 'h6'}
                sx={{ fontWeight: 700 }}
              >
                Product Help
              </Typography>
              <Chip
                size="small"
                label="Updated"
                variant="outlined"
                sx={{ color: '#5965A6' }}
              />
            </Box>

            <SearchField
              fullWidth
              placeholder="Search in help content"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {showEmbed && officeEmbedUrl && (
              <Box
                sx={{
                  mb: 2,
                  height: { xs: 360, sm: 480, md: 560 },
                  borderRadius: 1,
                  overflow: 'hidden',
                  boxShadow: 1,
                }}
              >
                <iframe
                  title="Help Handbook"
                  src={officeEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                />
              </Box>
            )}

            {filtered.length === 0 && !showEmbed && (
              <Typography color="text.secondary">
                No results. Try a different query.
              </Typography>
            )}

            {filtered.map((section) => (
              <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {section.title}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {section.items.map((it, idx) => (
                  <Box key={idx} sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 550, fontSize: '0.875rem' }}>
                      {it.q}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        tabSize: 4,
                      }}
                    >
                      {it.a}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}

            <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
              <Button
                onClick={openChat}
                variant="contained"
                sx={{ backgroundColor: '#5965A6' }}
              >
                Ask the AI Assistant
              </Button>
              <Button
                onClick={() => navigate('/contact')}
                variant="outlined"
                sx={{ borderColor: '#5965A6', color: '#5965A6' }}
              >
                Still need help? Contact us
              </Button>
              <IconButton
                onClick={() => scrollTo(filtered?.[0]?.id || sections[0].id)}
                aria-label="Scroll to top"
              >
                <ArrowUpwardIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
